import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { api, armStoredAccessTokenRefresh, clearScheduledAccessTokenRefresh, persistUiScope } from '../services/api';
import {
  clearAuthTokens,
  getRefreshCsrfToken,
  getStoredAccessToken,
  getStoredRefreshToken,
  hasBrowserRefreshSession,
  storeAccessTokenOnly,
  storeAuthTokens,
} from '../services/authStorage';
import { getPersonaPresetById } from '../auth/personaPresets';
import {
  clearStoredPreviewPersona,
  isPersonaPreviewFeatureEnabled,
  readStoredPreviewPersonaId,
  writeStoredPreviewPersonaId,
} from '../auth/previewMode';
import { canUsePersonaPreviewControls } from '../auth/access';

const AUTH_SESSION_HEADER =
  process.env.REACT_APP_AUTH_SESSION_HEADER || 'X-Edgentik-Session';
const AUTH_SESSION_MODE =
  process.env.REACT_APP_AUTH_SESSION_MODE || 'browser-cookie';

const LOGIN_REQUEST_TIMEOUT_MS = 25000;
const SESSION_REQUEST_TIMEOUT_MS = 30000;

function isPublicAuthRoute(pathname: string): boolean {
  return /^\/(login|activate|forgot-password|reset-password)(\/|$)/.test(String(pathname || ''));
}

export interface User {
  id: string;
  username: string;
  email: string;
  role: string;
  mfa_enabled?: boolean;
  capabilities?: string[];
  auth_source?: string | null;
  scope_mode?: string;
  scope_count?: number;
  primary_profile?: string | null;
  secondary_profiles?: string[];
  access_posture?: string | null;
}

function buildEffectiveUser(base: User | null, previewPersonaId: string | null): User | null {
  if (!base) return null;
  if (!previewPersonaId) return base;
  if (!isPersonaPreviewFeatureEnabled()) return base;
  if (!canUsePersonaPreviewControls(base)) return base;
  const preset = getPersonaPresetById(previewPersonaId);
  if (!preset) return base;
  return {
    ...base,
    capabilities: [...preset.capabilities],
    primary_profile: preset.primaryProfile ?? base.primary_profile,
    secondary_profiles: preset.secondaryProfiles ? [...preset.secondaryProfiles] : base.secondary_profiles,
  };
}

interface AuthContextType {
  /** Backend-authenticated user (never mutated for preview). */
  user: User | null;
  /** User shape used for frontend nav, routes, and affordances; may overlay persona preview capabilities. */
  effectiveUser: User | null;
  /** Active persona preset id when preview is on; null otherwise. */
  previewPersonaId: string | null;
  setPreviewPersona: (presetId: string | null) => void;
  clearPreviewPersona: () => void;
  refreshAuthzForCurrentScope: () => Promise<void>;
  login: (username: string, password: string, otpCode?: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  error: string | null;
}

type AuthzSnapshot = {
  role?: string;
  capabilities?: string[];
  source?: string | null;
  scope_mode?: string;
  scope_count?: number;
  primary_profile?: string | null;
  secondary_profiles?: string[];
  access_posture?: string | null;
};

function mergeUserAuthz(user: User, authz?: AuthzSnapshot | null): User {
  const authzCapabilities = authz?.capabilities;
  const authzScopeCount = authz?.scope_count;
  const authzPrimaryProfile = authz?.primary_profile;
  const authzSecondaryProfiles = authz?.secondary_profiles;
  const authzAccessPosture = authz?.access_posture;
  const secondaryProfiles = Array.isArray(authzSecondaryProfiles)
    ? authzSecondaryProfiles.map((value) => String(value || '').trim()).filter(Boolean)
    : user.secondary_profiles;
  return {
    ...user,
    role: authz?.role || user.role,
    capabilities:
      Array.isArray(authzCapabilities) && authzCapabilities.length > 0
        ? authzCapabilities
        : Array.isArray(user.capabilities)
          ? user.capabilities
          : [],
    auth_source: authz?.source || null,
    scope_mode: authz?.scope_mode || undefined,
    scope_count: typeof authzScopeCount === 'number' ? authzScopeCount : undefined,
    primary_profile: typeof authzPrimaryProfile === 'string' ? authzPrimaryProfile : user.primary_profile,
    secondary_profiles: secondaryProfiles,
    access_posture: typeof authzAccessPosture === 'string' ? authzAccessPosture : user.access_posture,
  };
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const [previewPersonaId, setPreviewPersonaId] = useState<string | null>(() => readStoredPreviewPersonaId());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const authRefreshRequestSeq = useRef(0);

  const effectiveUser = useMemo(() => buildEffectiveUser(user, previewPersonaId), [user, previewPersonaId]);

  const setPreviewPersona = useCallback((presetId: string | null) => {
    if (!presetId) {
      clearStoredPreviewPersona();
      setPreviewPersonaId(null);
      return;
    }
    if (!user || !isPersonaPreviewFeatureEnabled() || !canUsePersonaPreviewControls(user)) {
      clearStoredPreviewPersona();
      setPreviewPersonaId(null);
      return;
    }
    writeStoredPreviewPersonaId(presetId);
    setPreviewPersonaId(presetId);
  }, [user]);

  const clearPreviewPersona = useCallback(() => {
    clearStoredPreviewPersona();
    setPreviewPersonaId(null);
  }, []);

  useEffect(() => {
    if (!previewPersonaId) return;
    if (!user) return;
    if (isPersonaPreviewFeatureEnabled() && canUsePersonaPreviewControls(user)) return;
    clearStoredPreviewPersona();
    setPreviewPersonaId(null);
  }, [previewPersonaId, user]);

  const bootstrapScope = useCallback(async () => {
    try {
      const response = await api.get('/api/v1/scope/bootstrap', { timeout: 8000 });
      if (response.data?.ok) {
        persistUiScope(response.data.org_id, response.data.site_id);
      }
    } catch {
      // best-effort
    }
  }, []);

  const fetchAndMergeCurrentSession = useCallback(async (opts?: { clearSessionOnFailure?: boolean }) => {
    const requestId = ++authRefreshRequestSeq.current;
    try {
      const response = await api.get('/api/v1/auth/me', { timeout: SESSION_REQUEST_TIMEOUT_MS });
      if (requestId !== authRefreshRequestSeq.current) return null;
      const mergedUser = mergeUserAuthz(response.data.user, response.data.authz);
      setUser(mergedUser);
      void bootstrapScope();
      return mergedUser;
    } catch (err) {
      if (opts?.clearSessionOnFailure && requestId === authRefreshRequestSeq.current) {
        clearAuthTokens();
        delete api.defaults.headers.common['Authorization'];
        clearStoredPreviewPersona();
        setPreviewPersonaId(null);
        setUser(null);
      }
      return null;
    }
  }, [bootstrapScope]);

  const validateAndRestoreSession = useCallback(async (_token: string) => {
    try {
      await fetchAndMergeCurrentSession({ clearSessionOnFailure: true });
    } catch (err) {
      clearAuthTokens();
      delete api.defaults.headers.common['Authorization'];
      clearStoredPreviewPersona();
      setPreviewPersonaId(null);
    } finally {
      setIsLoading(false);
    }
  }, [fetchAndMergeCurrentSession]);

  const refreshAuthzForCurrentScope = useCallback(async () => {
    const hasSession = Boolean(getStoredAccessToken() || hasBrowserRefreshSession());
    if (!hasSession) return;
    await fetchAndMergeCurrentSession({ clearSessionOnFailure: false });
  }, [fetchAndMergeCurrentSession]);

  const refreshAndRestoreSession = useCallback(async () => {
    try {
      const refreshToken = getStoredRefreshToken();
      const csrfToken = getRefreshCsrfToken();
      const usingBrowserRefreshSession = Boolean(csrfToken);
      if (!refreshToken && !usingBrowserRefreshSession) {
        setIsLoading(false);
        return;
      }
      const headers: Record<string, string> = {};
      if (refreshToken) {
        headers.Authorization = `Bearer ${refreshToken}`;
      }
      if (usingBrowserRefreshSession) {
        headers[AUTH_SESSION_HEADER] = AUTH_SESSION_MODE;
        headers['X-CSRF-TOKEN'] = csrfToken;
      }
      const response = await api.post(
        '/api/v1/auth/refresh',
        {},
        {
          headers,
          timeout: SESSION_REQUEST_TIMEOUT_MS,
          withCredentials: usingBrowserRefreshSession,
        }
      );
      const accessToken = String(response.data?.access_token || '').trim();
      const nextRefreshToken = String(response.data?.refresh_token || '').trim();
      if (!accessToken) {
        clearAuthTokens();
        delete api.defaults.headers.common['Authorization'];
        setIsLoading(false);
        return;
      }
      if (usingBrowserRefreshSession && !nextRefreshToken) {
        storeAccessTokenOnly(accessToken);
      } else if (nextRefreshToken) {
        storeAuthTokens(accessToken, nextRefreshToken);
      } else {
        clearAuthTokens();
        delete api.defaults.headers.common['Authorization'];
        setIsLoading(false);
        return;
      }
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      await validateAndRestoreSession(accessToken);
    } catch {
      clearAuthTokens();
      delete api.defaults.headers.common['Authorization'];
      setIsLoading(false);
    }
  }, [validateAndRestoreSession]);

  useEffect(() => {
    if (isPublicAuthRoute(location.pathname)) {
      clearScheduledAccessTokenRefresh();
      setIsLoading(false);
      return;
    }
    const token = getStoredAccessToken();
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      armStoredAccessTokenRefresh();
      validateAndRestoreSession(token);
    } else if (getStoredRefreshToken() || hasBrowserRefreshSession()) {
      refreshAndRestoreSession();
    } else {
      setIsLoading(false);
    }
  }, [location.pathname, refreshAndRestoreSession, validateAndRestoreSession]);

  useEffect(() => {
    const handleScopeChanged = () => {
      void refreshAuthzForCurrentScope();
    };
    window.addEventListener('edgentik:scope-changed', handleScopeChanged);
    return () => window.removeEventListener('edgentik:scope-changed', handleScopeChanged);
  }, [refreshAuthzForCurrentScope]);

  const login = async (username: string, password: string, otpCode?: string) => {
    try {
      setError(null);

      // Do not request browser-cookie mode. The refresh CSRF cookie is Path=/api/v1/auth/,
      // so the SPA cannot see it and the 401 interceptor never refreshes. The API returns
      // refresh_token in the body when that header is omitted; sessionStorage holds it and
      // the shared refresh path renews the 60-minute access token before exp.
      const response = await api.post(
        '/api/v1/auth/login',
        { username, password, ...(otpCode ? { otp_code: otpCode } : {}) },
        {
          timeout: LOGIN_REQUEST_TIMEOUT_MS,
          withCredentials: true,
        }
      );
      const { access_token, refresh_token, user: userData, authz } = response.data;

      if (String(refresh_token || '').trim()) {
        storeAuthTokens(access_token, refresh_token);
      } else {
        storeAccessTokenOnly(access_token);
      }
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      armStoredAccessTokenRefresh();
      setUser(mergeUserAuthz(userData, authz));
      void bootstrapScope();

      // Hydrate capabilities from /auth/me before callers navigate away from /login.
      await fetchAndMergeCurrentSession({ clearSessionOnFailure: false }).catch(() => {
        // Keep the login payload; primary_profile is enough for external reviewer routing.
      });
    } catch (err: any) {
      const msg = err.response?.data?.error;
      if (msg) {
        if (msg === 'mfa_required') {
          setError('Multi-factor code required.');
        } else if (msg === 'invalid_mfa_code') {
          setError('Invalid multi-factor code.');
        } else {
          setError(msg);
        }
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError(
          'Backend took too long to respond. The cloud API may be overloaded (high incident volume) or restarting. Wait a moment and try again, or check: docker compose logs cloud_api',
        );
      } else if (err.message) {
        setError(err.message || 'Login failed');
      } else {
        setError('Login failed');
      }
      throw err;
    }
  };

  const logout = () => {
    const refreshToken = getStoredRefreshToken();
    const csrfToken = getRefreshCsrfToken();
    const headers: Record<string, string> = {};
    if (csrfToken) {
      headers[AUTH_SESSION_HEADER] = AUTH_SESSION_MODE;
      headers['X-CSRF-TOKEN'] = csrfToken;
    }
    api
      .post(
        '/api/v1/auth/logout',
        refreshToken ? { refresh_token: refreshToken } : {},
        { headers, withCredentials: Boolean(csrfToken) }
      )
      .catch(() => undefined);
    clearAuthTokens();
    delete api.defaults.headers.common['Authorization'];
    clearStoredPreviewPersona();
    setPreviewPersonaId(null);
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    effectiveUser,
    previewPersonaId,
    setPreviewPersona,
    clearPreviewPersona,
    refreshAuthzForCurrentScope,
    login,
    logout,
    isLoading,
    error,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
